import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'

import { STORAGE_KEYS } from '@/lib/configs/storage-config'
import { safeStorage } from '@/lib/safe-storage'
import type {
  ClassifiedNotification,
  NotificationCategory,
} from '@/service/ws/classify-notification'
import { type ResearchNotificationEntry, researchNotificationsAtom } from '@/state/research'
import type { ServiceNotification } from '@/types/generated/notifications'

export interface ServiceNotificationHistoryEntry extends ClassifiedNotification {
  id: string
  source: 'service'
  timestamp: number
  accountId: string
  raw: ServiceNotification
}

export type NotificationHistoryEntry = ServiceNotificationHistoryEntry | ResearchNotificationEntry

const HISTORY_CAP = 100

/** Newest-first ring buffer, capped at HISTORY_CAP entries, across every account seen
 * this session. Session-only — deliberately a plain atom, not atomWithStorage; history
 * does not survive a page reload. Not read directly by UI — see notificationHistoryAtom. */
export const notificationHistoryRawAtom = atom<ServiceNotificationHistoryEntry[]>([])

/** Unfiltered across every account — the bell/panel show every connected account's
 * notifications together (each entry carries its own accountId for the UI to label).
 * Setting (the panel's Clear button) clears all of them, mirroring state/account.ts's
 * allPositionsAtom/allBalancesAtom convention: there's no "currently active account" to
 * partially clear against anymore. */
export const notificationHistoryAtom = atom(
  (get) =>
    [
      ...get(notificationHistoryRawAtom),
      ...get(researchNotificationsAtom).filter((entry) => !entry.dismissedAt),
    ].sort((a, b) => b.timestamp - a.timestamp),
  (get, set) => {
    set(notificationHistoryRawAtom, [])
    const now = Date.now()
    set(
      researchNotificationsAtom,
      get(researchNotificationsAtom).map((entry) => ({ ...entry, dismissedAt: now }))
    )
  }
)

/** When the bell was last opened/closed. Module-level so it survives
 * NotificationBell unmounting when the user navigates away from /trade and
 * back — component-local state would reset to 0 on remount and mark every
 * already-seen entry unread again. */
export const notificationLastSeenAtAtom = atom<number>(0)

/** Identifies "the same underlying event" independent of our own randomUUID id, so a
 * redelivered WS message (reconnect resend, at-least-once NATS delivery, or a duplicate
 * publish) doesn't show up as two history rows.
 *
 * Deliberately does NOT use `entry.timestamp` as the identity: every backend notification
 * constructor (services/algo's `publish_lifecycle`, and the per-venue trade_venue
 * account_instance.rs for Deribit/Hyperliquid/Upbit/Bybit, and public-api's
 * session_security.rs) stamps `timestamp` with the wall clock at *publish* time — it is
 * never the exchange's original event time. A duplicate publish of the same underlying
 * event therefore carries two different timestamps and would slip past a timestamp-keyed
 * dedupe every time, so each variant below keys on fields intrinsic to the event itself. */
function notificationDedupeKey(entry: ServiceNotificationHistoryEntry): string {
  const { accountId, raw } = entry
  const { orderResponse, orderValidationError, orderExecutionError, algoLifecycle, securityAlert } =
    raw

  if (orderResponse) {
    // orderId+status alone would wrongly collapse two distinct real partial fills of the
    // same order (both PARTIALLY_FILLED) — filledQuantity/averagePrice/errorMessage
    // differentiate a genuinely new fill from a redelivery of the same one.
    const { orderId, status, filledQuantity, averagePrice, errorMessage } = orderResponse
    return `${accountId}:order:${orderId}:${status}:${filledQuantity ?? ''}:${averagePrice ?? ''}:${errorMessage ?? ''}`
  }
  if (orderValidationError) {
    const { symbol, errorMessage, orderParams } = orderValidationError
    return `${accountId}:order_validation:${orderParams?.correlationId ?? ''}:${symbol}:${errorMessage}`
  }
  if (orderExecutionError) {
    const { correlationId, errorMessage } = orderExecutionError
    return `${accountId}:order_execution:${correlationId ?? ''}:${errorMessage}`
  }
  // An algo can only start/complete/cancel/fail once, so (algoId, status) recurring is
  // always a duplicate publish, never two distinct real transitions.
  if (algoLifecycle) {
    return `${accountId}:algo:${algoLifecycle.algoId}:${algoLifecycle.status}`
  }
  if (securityAlert) {
    return `${accountId}:security:${securityAlert.message}`
  }
  // No oneof variant recognized — fall back to the volatile timestamp; better to risk an
  // unlikely false negative here than to silently drop a notification we can't identify.
  return `${accountId}:${entry.category}:${entry.timestamp}`
}

/** Pure so it's unit-testable without touching jotai. */
export function pushNotification(
  history: ServiceNotificationHistoryEntry[],
  entry: ServiceNotificationHistoryEntry,
  cap: number = HISTORY_CAP
): ServiceNotificationHistoryEntry[] {
  const key = notificationDedupeKey(entry)
  if (history.some((existing) => notificationDedupeKey(existing) === key)) return history
  return [entry, ...history].sort((a, b) => b.timestamp - a.timestamp).slice(0, cap)
}

export type FilterableCategory = Exclude<NotificationCategory, 'security' | 'unknown'>

export type NotificationFilters = Record<FilterableCategory, boolean>

export const FILTERABLE_NOTIFICATION_CATEGORIES: FilterableCategory[] = [
  'order_placed',
  'order_filled',
  'order_cancelled',
  'order_error',
  'algo',
  'research',
]

export function isNotificationCategoryEnabled(
  category: NotificationCategory,
  filters: NotificationFilters
): boolean {
  // Security and unknown notifications are intentionally always visible.
  if (category === 'security' || category === 'unknown') return true
  return filters[category]
}

const DEFAULT_NOTIFICATION_FILTERS: NotificationFilters = {
  order_placed: true,
  order_filled: true,
  order_cancelled: true,
  order_error: true,
  algo: true,
  research: true,
}

/** `security` has no entry — it's never filterable, always shown in history. */
export const notificationFiltersAtom = atomWithStorage<NotificationFilters>(
  STORAGE_KEYS.notifications.filters,
  DEFAULT_NOTIFICATION_FILTERS,
  safeStorage,
  { getOnInit: true }
)
